/**
 * types.ts
 * 
 * 职责：
 * 1. 定义整个应用程序中使用的所有类型、接口和枚举
 * 2. 提供画布模式(CanvasMode)和形状类型(ShapeType)的枚举
 * 3. 定义几何相关的接口，如点(Point)和边界(Bounds)
 * 4. 定义绘图元素的数据结构，如线条(LineData)和文本(TextData)
 * 5. 定义上下文菜单状态(ContextMenuState)和画布尺寸(CanvasSize)接口
 * 6. 集中管理所有类型定义，确保类型一致性和可维护性
 */

export enum CanvasMode {
  DRAW = 'draw',
  TEXT = 'text',
  SELECT = 'select',
  SHAPE = 'shape',
  CARD = 'card',
  IMAGE = 'image'
}

export enum ShapeType {
  ROUNDED_RECTANGLE = 'rounded-rectangle',
  RECTANGLE = 'rectangle',
  CIRCLE = 'circle',
  TRIANGLE = 'triangle',
  LINE = 'line'
}

export interface Point {
  x: number;
  y: number;
}

export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface LineData {
  points: Point[];
  color: string;
  size: number;
  path: Path2D;
  lineCap?: CanvasLineCap;
  lineJoin?: CanvasLineJoin;
  isConvertedShape?: boolean;
  originalShapeType?: ShapeType;
  shapeStartPoint?: Point;
  shapeEndPoint?: Point;
}

// 可序列化的LineData，用于保存到localStorage
export interface SerializableLineData {
  points: Point[];
  color: string;
  size: number;
  lineCap?: CanvasLineCap;
  lineJoin?: CanvasLineJoin;
  isConvertedShape?: boolean;
  originalShapeType?: ShapeType;
  shapeStartPoint?: Point;
  shapeEndPoint?: Point;
}

export interface TextData {
  text: string;
  baseX: number;  // 文本绘制的基线起点X坐标
  baseY: number;  // 文本绘制的基线起点Y坐标
  boxX?: number;  // 文本框的左上角X坐标
  boxY?: number;  // 文本框的左上角Y坐标
  boxWidth?: number; // 文本框宽度
  boxHeight?: number; // 文本框高度
  color: string;//文本颜色
  fontSize: number;//文本大小
  isEditing?: boolean;//是否正在编辑
  creationScale?: number;//创建比例
  editorScreenX?: number;//编辑器屏幕X坐标
  editorScreenY?: number;//编辑器屏幕Y坐标
  inputWidth?: number;//输入框宽度
  inputHeight?: number;//输入框高度
  lineCount?: number;//行数
  offsetX?: number;//偏移X
  offsetY?: number;//偏移Y
  annotation?: string; // 文本的注释内容
  hasAnnotation?: boolean; // 是否有注释
}

export interface ContextMenuState {
  visible: boolean;
  x: number;
  y: number;
  type?: 'line' | 'text' | 'card' | 'empty' | 'group' | 'image'; // 上下文菜单类型
  textIndex?: number; // 文本索引
  lineIndex?: number; // 线条索引
  cardIndex?: number; // 卡片索引
  imageIndex?: number; // 图片索引
  groupId?: string; // 组合ID
}

export interface CanvasSize {
  width: number;
  height: number;
}

export interface CardData {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  content: string;
  color: string;
  borderColor: string;
  borderRadius: number;
  isEditing?: boolean;
  creationScale?: number;
}

export interface ImageData {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  src: string;        // 图片源URL
  originalWidth: number; // 原始宽度
  originalHeight: number; // 原始高度
  aspectRatio: number;  // 宽高比
  alt?: string;        // 图片描述
  creationScale?: number; // 创建时的缩放比例
  rotation?: number;    // 旋转角度（弧度）
  filters?: {          // 图片滤镜
    brightness?: number;
    contrast?: number;
    saturation?: number;
    blur?: number;
  };
}

export interface CanvasContent {
  lines: SerializableLineData[];
  texts: TextData[];
  cards: CardData[];
  images: ImageData[];
  groups: GroupData[];
  noteId: string;
  width: number;
  height: number;
  name: string;
  createdAt: number;
  viewport?: {
    scale: number;
    offsetX: number;
    offsetY: number;
  };
}

// 组合数据接口
export interface GroupData {
  id: string;
  elementIds: GroupElement[];
  name?: string;
}

// 组合元素类型
export interface GroupElement {
  type: 'line' | 'text' | 'card' | 'image';
  index: number;
} 